/**
 * React Query hooks for user operations
 */

import type { CreateUserInput, UpdateUserInput, User, UserConnection, UserStatus } from "@eoln/userpool/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";

const USERS_QUERY_KEY = "users";
const USER_QUERY_KEY = "user";

export interface UseUsersOptions {
  limit?: number;
  offset?: number;
  status?: UserStatus;
}

/**
 * Fetch paginated list of users
 */
export function useUsers(options: UseUsersOptions = {}) {
  const { client } = useAuth();
  const { limit = 10, offset = 0, status } = options;

  return useQuery<UserConnection>({
    queryKey: [USERS_QUERY_KEY, { limit, offset, status }],
    queryFn: async () => {
      if (!client) {
        throw new Error("Not authenticated");
      }
      return client.listUsers({
        pagination: { limit, offset },
        status,
      });
    },
    enabled: !!client,
  });
}

/**
 * Fetch single user by ID
 */
export function useUser(userId: string | undefined) {
  const { client } = useAuth();

  return useQuery<User | null>({
    queryKey: [USER_QUERY_KEY, userId],
    queryFn: async () => {
      if (!client || !userId) {
        throw new Error("Not authenticated or missing userId");
      }
      return client.getUser(userId);
    },
    enabled: !!client && !!userId,
  });
}

/**
 * Create a new user
 */
export function useCreateUser() {
  const { client } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<User, Error, { input: CreateUserInput; skipVerification?: boolean }>({
    mutationFn: async ({ input, skipVerification }) => {
      if (!client) {
        throw new Error("Not authenticated");
      }
      return client.createUser(input, skipVerification);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
}

/**
 * Update user details
 */
export function useUpdateUser() {
  const { client } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<User, Error, { userId: string; input: UpdateUserInput }>({
    mutationFn: async ({ userId, input }) => {
      if (!client) {
        throw new Error("Not authenticated");
      }
      return client.updateUser(userId, input);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY, variables.userId] });
    },
  });
}

/**
 * Delete user
 */
export function useDeleteUser() {
  const { client } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, string>({
    mutationFn: async (userId) => {
      if (!client) {
        throw new Error("Not authenticated");
      }
      return client.deleteUser(userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
}

/**
 * Activate a pending user
 */
export function useActivateUser() {
  const { client } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<User, Error, string>({
    mutationFn: async (userId) => {
      if (!client) {
        throw new Error("Not authenticated");
      }
      return client.activateUser(userId);
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY, userId] });
    },
  });
}

/**
 * Suspend a user
 */
export function useSuspendUser() {
  const { client } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<User, Error, { userId: string; reason?: string }>({
    mutationFn: async ({ userId, reason }) => {
      if (!client) {
        throw new Error("Not authenticated");
      }
      return client.suspendUser(userId, reason);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY, variables.userId] });
    },
  });
}

/**
 * Unsuspend a user
 */
export function useUnsuspendUser() {
  const { client } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<User, Error, string>({
    mutationFn: async (userId) => {
      if (!client) {
        throw new Error("Not authenticated");
      }
      return client.unsuspendUser(userId);
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY, userId] });
    },
  });
}

/**
 * Disable a user
 */
export function useDisableUser() {
  const { client } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<User, Error, { userId: string; reason?: string }>({
    mutationFn: async ({ userId, reason }) => {
      if (!client) {
        throw new Error("Not authenticated");
      }
      return client.disableUser(userId, reason);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY, variables.userId] });
    },
  });
}

/**
 * Enable a disabled user
 */
export function useEnableUser() {
  const { client } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<User, Error, string>({
    mutationFn: async (userId) => {
      if (!client) {
        throw new Error("Not authenticated");
      }
      return client.enableUser(userId);
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY, userId] });
    },
  });
}

/**
 * Unlock a locked user
 */
export function useUnlockUser() {
  const { client } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<User, Error, string>({
    mutationFn: async (userId) => {
      if (!client) {
        throw new Error("Not authenticated");
      }
      return client.unlockUser(userId);
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY, userId] });
    },
  });
}
